import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import {
	userService,
	TicketData,
	TicketGroupResponse,
} from "../../services/userService";
import {
	FiCalendar,
	FiMapPin,
	FiTag,
	FiClock,
	FiCheckCircle,
} from "react-icons/fi";
import { HiTicket } from "react-icons/hi";
import {
	formatJapaneseDateWithWeekday,
	formatJapaneseTime,
} from "../../utils/dateUtils";
import { formatJapaneseAddress } from "../../utils/addressUtils";
import TicketQrModal from "./TicketQrModal";

type TicketGroup = {
	eventId: number;
	eventTitle: string;
	eventLocation: string;
	eventAddress?: string | null;
	eventPrefecture?: string | null;
	eventCity?: string | null;
	startDate?: string | null;
	endDate?: string | null;
	startTime?: string | null;
	endTime?: string | null;
	eventImageUrl?: string | null;
	tickets: TicketData[];
};

type TicketTypeBundle = {
	ticketTypeLabel: string;
	tickets: TicketData[];
};

type ActiveTicketModalData = {
	eventTitle: string;
	eventLocation?: string;
	eventImageUrl?: string | null;
	scheduleLabel?: string;
	ticketType: string;
	tickets: {
		orderItemId: number;
		ticketId: number;
		qrToken: string;
		orderQrToken: string;
		sequenceNumber: number;
		status: "valid" | "redeemed";
	}[];
};

const hasExplicitTime = (value?: string | null): boolean => {
	if (!value) return false;
	return /(\d{1,2}):(\d{2})/.test(value);
};

const parseDateSafely = (value?: string | null): Date | null => {
	if (!value) return null;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getTimeValue = (value?: string | null): number => {
	const parsed = parseDateSafely(value);
	return parsed ? parsed.getTime() : Number.MAX_SAFE_INTEGER;
};

const isEventActive = (group: TicketGroup): boolean => {
	const now = new Date();
	const endDate = parseDateSafely(group.endDate ?? group.startDate ?? null);
	if (endDate) {
		return endDate >= now;
	}
	return true;
};

const MyTickets: React.FC = () => {
	const { isAuthenticated } = useAuth();
	const [tickets, setTickets] = useState<TicketData[]>([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState<"all" | "active" | "past">("all");
	const [activeTicketBundle, setActiveTicketBundle] =
		useState<ActiveTicketModalData | null>(null);

	const syncTicketStatuses = useCallback((payload: TicketGroupResponse) => {
		setTickets((prev) =>
			prev.map((ticket) => {
				if (ticket.orderItemId !== payload.orderItemId) {
					return ticket;
				}
				const matching =
					payload.tickets.find((t) => t.id === ticket.id) ||
					payload.tickets.find(
						(t) => t.sequenceNumber === ticket.sequenceNumber
					);
				if (!matching) {
					return ticket;
				}
				return {
					...ticket,
					status: matching.status,
					isValid: matching.status === "valid",
				};
			})
		);

		setActiveTicketBundle((prev) => {
			if (!prev) {
				return prev;
			}
			let updated = false;
			const nextTickets = prev.tickets.map((ticket) => {
				if (ticket.orderItemId !== payload.orderItemId) {
					return ticket;
				}
				const matching =
					payload.tickets.find((t) => t.id === ticket.ticketId) ||
					payload.tickets.find(
						(t) => t.sequenceNumber === ticket.sequenceNumber
					);
				if (!matching) {
					return ticket;
				}
				updated = true;
				return {
					...ticket,
					status: matching.status,
				};
			});
			return updated ? { ...prev, tickets: nextTickets } : prev;
		});
	}, []);

	useEffect(() => {
		const fetchTickets = async () => {
			if (!isAuthenticated) return;

			try {
				const ticketData = await userService.getTickets();
				setTickets(ticketData);
				setLoading(false);
			} catch (error) {
				console.error("Error fetching tickets:", error);
				toast.error("チケット情報の取得に失敗しました");
				setLoading(false);
			}
		};

		fetchTickets();
	}, [isAuthenticated]);

	// Filter tickets based on selected filter
	const filteredTickets = useMemo(() => {
		return tickets.filter((ticket) => {
			if (filter === "all") return true;
			if (!ticket.startDate) return true;
			const ticketDate = parseDateSafely(ticket.startDate);
			if (!ticketDate) return true;
			const now = new Date();
			if (filter === "active") return ticketDate >= now;
			if (filter === "past") return ticketDate < now;
			return true;
		});
	}, [tickets, filter]);

	const groupedTickets = useMemo(() => {
		const map = new Map<number, TicketGroup>();
		filteredTickets.forEach((ticket) => {
			if (!map.has(ticket.eventId)) {
				map.set(ticket.eventId, {
					eventId: ticket.eventId,
					eventTitle: ticket.eventTitle,
					eventLocation: ticket.eventLocation,
					eventAddress: ticket.eventAddress,
					eventPrefecture: ticket.eventPrefecture,
					eventCity: ticket.eventCity,
					startDate: ticket.startDate,
					endDate: ticket.endDate,
					startTime: ticket.startTime,
					endTime: ticket.endTime,
					eventImageUrl: ticket.eventImageUrl,
					tickets: [],
				});
			}
			const group = map.get(ticket.eventId)!;
			if (!group.eventImageUrl && ticket.eventImageUrl) {
				group.eventImageUrl = ticket.eventImageUrl;
			}
			if (!group.startDate && ticket.startDate) {
				group.startDate = ticket.startDate;
			}
			if (!group.endDate && ticket.endDate) {
				group.endDate = ticket.endDate;
			}
			if (!group.startTime && ticket.startTime) {
				group.startTime = ticket.startTime;
			}
			if (!group.endTime && ticket.endTime) {
				group.endTime = ticket.endTime;
			}
			if (!group.eventPrefecture && ticket.eventPrefecture) {
				group.eventPrefecture = ticket.eventPrefecture;
			}
			if (!group.eventCity && ticket.eventCity) {
				group.eventCity = ticket.eventCity;
			}
			if (!group.eventAddress && ticket.eventAddress) {
				group.eventAddress = ticket.eventAddress;
			}
			group.tickets.push(ticket);
		});

		return Array.from(map.values()).sort(
			(a, b) => getTimeValue(a.startDate) - getTimeValue(b.startDate)
		);
	}, [filteredTickets]);

	if (loading) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
						<p className="mt-4 text-gray-600 dark:text-gray-400">
							チケットを読み込み中...
						</p>
					</div>
				</div>
			</div>
		);
	}

	if (tickets.length === 0) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="space-y-6">
					{/* Header */}
					<div className="flex justify-between items-center">
						<div>
							<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
								マイチケット
							</h2>
							<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
								購入済みチケットの確認と管理
							</p>
						</div>
					</div>

					{/* Empty State */}
					<div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-lg">
						<div className="p-6 bg-gray-100 dark:bg-gray-700 rounded-full mb-6 inline-block">
							<HiTicket className="h-12 w-12 text-gray-400 dark:text-gray-500" />
						</div>
						<p className="text-gray-500 dark:text-gray-400">
							チケットはありません
						</p>
						<a
							href="/"
							className="mt-4 inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-lg transition-all duration-200"
						>
							イベントを探す
						</a>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="space-y-6">
					{/* Header */}
					<div className="flex justify-between items-center">
						<div>
							<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
								マイチケット
							</h2>
							<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
								購入済みチケットの確認と管理
							</p>
						</div>
					</div>

					{/* Filter Tabs */}
					<div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700 pb-2">
						<button
							onClick={() => setFilter("all")}
							className={`px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 ${
								filter === "all"
									? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm"
									: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
							}`}
						>
							すべて ({tickets.length})
						</button>
						<button
							onClick={() => setFilter("active")}
							className={`px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 ${
								filter === "active"
									? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm"
									: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
							}`}
						>
							有効
						</button>
						<button
							onClick={() => setFilter("past")}
							className={`px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 ${
								filter === "past"
									? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm"
									: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
							}`}
						>
							過去
						</button>
					</div>

					{/* Tickets Overview */}
					{groupedTickets.length === 0 ? (
						<div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-gray-500 dark:text-gray-400">
							<p>現在選択中のフィルターに該当するチケットはありません。</p>
						</div>
					) : (
						<div className="space-y-6">
							{groupedTickets.map((group) => {
								const isActive = isEventActive(group);
								const statusStyles = isActive
									? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
									: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
								const formattedStartDate = formatJapaneseDateWithWeekday(
									group.startDate ?? group.tickets[0]?.startDate
								);
								const formattedEndDate =
									group.endDate && group.endDate !== group.startDate
										? formatJapaneseDateWithWeekday(group.endDate)
										: "";
								const startTimeValue = hasExplicitTime(group.startTime)
									? group.startTime
									: hasExplicitTime(group.startDate)
									? group.startDate
									: undefined;
								const endTimeValue = hasExplicitTime(group.endTime)
									? group.endTime
									: hasExplicitTime(group.endDate)
									? group.endDate
									: undefined;
								const startTimeLabel = startTimeValue
									? formatJapaneseTime(startTimeValue)
									: "";
								const endTimeLabel = endTimeValue
									? formatJapaneseTime(endTimeValue)
									: "";
								const addressLabel = formatJapaneseAddress({
									prefecture: group.eventPrefecture,
									city: group.eventCity,
									street: group.eventAddress,
									fallback: group.eventAddress,
								});
								const ticketTypeBundles = Array.from(
									group.tickets.reduce((map, ticket) => {
										const label = ticket.ticketType || "未分類チケット";
										if (!map.has(label)) {
											map.set(label, {
												ticketTypeLabel: label,
												tickets: [],
											});
										}
										map.get(label)!.tickets.push(ticket);
										return map;
									}, new Map<string, TicketTypeBundle>())
								).map(([, value]) => value);

								return (
									<div
										key={group.eventId}
										className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm"
									>
										<div className="flex flex-col lg:flex-row gap-6 p-6">
											{group.eventImageUrl && (
												<div className="w-full lg:w-56 overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-700">
													<img
														src={group.eventImageUrl}
														alt={group.eventTitle}
														className="h-48 w-full object-cover"
													/>
												</div>
											)}
											<div className="flex-1 space-y-5">
												<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
													<div>
														<p className="text-xs uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
															Event
														</p>
														<h3 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
															{group.eventTitle}
														</h3>
													</div>
													<div className="flex flex-col items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
														<span
															className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${statusStyles}`}
														>
															{isActive ? (
																<FiCheckCircle className="h-3.5 w-3.5 mr-1" />
															) : (
																<FiClock className="h-3.5 w-3.5 mr-1" />
															)}
															{isActive ? "有効" : "終了"}
														</span>
														<span className="text-xs text-gray-500 dark:text-gray-400">
															チケット {group.tickets.length} 枚
														</span>
													</div>
												</div>

												<div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
													<div className="flex items-start gap-3">
														<FiCalendar className="h-4 w-4 flex-shrink-0 mt-0.5" />
														<div>
															<p className="font-semibold text-gray-900 dark:text-white">
																{formattedStartDate ||
																	group.startDate ||
																	"日程未定"}
																{formattedEndDate && (
																	<span className="text-sm text-gray-500 dark:text-gray-400">
																		{" "}
																		〜 {formattedEndDate}
																	</span>
																)}
															</p>
															{startTimeLabel && (
																<p className="text-xs text-gray-500 dark:text-gray-400">
																	{startTimeLabel}
																	{endTimeLabel && (
																		<span>{` 〜 ${endTimeLabel}`}</span>
																	)}
																</p>
															)}
														</div>
													</div>
													<div className="flex items-start gap-3">
														<FiMapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
														<div>
															<p className="font-semibold text-gray-900 dark:text-white">
																{group.eventLocation}
															</p>
															{addressLabel && (
																<p className="text-xs text-gray-500 dark:text-gray-400">
																	{addressLabel}
																</p>
															)}
														</div>
													</div>
												</div>

												<div className="space-y-3">
													{ticketTypeBundles.map((typeBundle) => {
														const scheduleLabelValue =
															[
																formattedStartDate || group.startDate || "",
																startTimeLabel,
															]
																.filter(Boolean)
																.join(" ")
																.trim() || undefined;
														const total = typeBundle.tickets.length;
														const unused = typeBundle.tickets.filter(
															(t) => t.status === "valid"
														).length;
														return (
															<div
																key={`${group.eventId}-${typeBundle.ticketTypeLabel}`}
																className="flex flex-col gap-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 p-4 sm:flex-row sm:items-center sm:justify-between"
															>
																<div className="flex items-start gap-3">
																	<div className="mt-0.5 text-blue-500 dark:text-blue-300">
																		<FiTag className="h-4 w-4" />
																	</div>
																	<div>
																		<p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
																			チケット種別
																		</p>
																		<p className="text-base font-semibold text-gray-900 dark:text-white">
																			{typeBundle.ticketTypeLabel}
																		</p>
																		<p className="text-xs text-gray-500 dark:text-gray-400">
																			未使用 {unused} / {total} 枚
																		</p>
																		{typeBundle.tickets.length > 1 && (
																			<p className="text-[11px] text-gray-400">
																				QR {typeBundle.tickets.length} 個
																			</p>
																		)}
																	</div>
																</div>
																<div className="sm:w-64">
																	<button
																		onClick={() =>
																			setActiveTicketBundle({
																				eventTitle: group.eventTitle,
																				eventLocation: group.eventLocation,
																				eventImageUrl: group.eventImageUrl,
																				scheduleLabel: scheduleLabelValue,
																				ticketType: typeBundle.ticketTypeLabel,
																				tickets: typeBundle.tickets.map(
																					(ticket) => ({
																						orderItemId: ticket.orderItemId,
																						ticketId: ticket.id,
																						qrToken: ticket.qrToken,
																						orderQrToken:
																							ticket.orderQrToken ||
																							ticket.qrToken,
																						sequenceNumber:
																							ticket.sequenceNumber,
																						status: ticket.status,
																					})
																				),
																			})
																		}
																		className="w-full flex items-center justify-center gap-3 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
																	>
																		<span>チケットを表示</span>
																		<span className="inline-flex items-center justify-center px-3 py-0.5 rounded-full border border-white/50 bg-white/10 text-xs font-semibold">
																			{total}枚
																		</span>
																	</button>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
			{activeTicketBundle && (
				<TicketQrModal
					tickets={activeTicketBundle.tickets}
					onClose={() => setActiveTicketBundle(null)}
					eventTitle={activeTicketBundle.eventTitle}
					eventLocation={activeTicketBundle.eventLocation}
					eventImageUrl={activeTicketBundle.eventImageUrl}
					scheduleLabel={activeTicketBundle.scheduleLabel}
					ticketType={activeTicketBundle.ticketType}
					onStatusChange={syncTicketStatuses}
				/>
			)}
		</>
	);
};

export default MyTickets;
